import { Component } from '@theme/component';

// Only explicit product titles or merchant-owned metafields are used. Tags are not compatibility evidence.
const brands = [['Volkswagen', /\b(?:volkswagen|vw)\b/i], ['BMW', /\bbmw\b/i], ['Audi', /\baudi\b/i], ['Mercedes', /\bmercedes\b/i], ['Peugeot', /\bpeugeot\b/i], ['Citroën', /\bcitro[eë]n\b/i], ['DS', /\bds\b/i], ['Renault', /\brenault\b/i], ['Fiat', /\bfiat\b/i], ['Kia', /\bkia\b/i], ['Tesla', /\btesla\b/i]];
const systems = /\b(?:CCC|CIC|NBT|SMEG\+?|MRN|NTG\s?\d(?:\.\d)?|MMI\s?\d?[Gg]?)\b/g;
const models = /\b(?:Polo\s?5(?:\s+6R\s?\/\s?6C)?|Golf\s?[567]|Passat\s?B[67](?:\s?\/\s?B[67])?|Clio\s?[24]|Sportage\s?4(?:\s?QL)?|A[34]\s?B?8|A3\s?8P|W204|208\s?\/\s?2008|208|2008|508|500\s?\/\s?500C|500C?|[EXY]\d{2}|Model[eè]?\s?[3SXY])\b/gi;
const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();

/** @typedef {{panel: HTMLDetailsElement, brand: HTMLSelectElement, model: HTMLSelectElement, solution: HTMLSelectElement, maxPrice: HTMLInputElement, stock: HTMLInputElement, sort: HTMLSelectElement, status: HTMLElement, active: HTMLElement, grid: HTMLUListElement, empty: HTMLElement}} BrowserRefs */
/** @extends {Component<BrowserRefs>} */
class VehicleBrowser extends Component {
  requiredRefs = ['brand','model','solution','grid','status'];
  #abort;
  #items = [];
  #updating = false;
  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#initialize();
    window.addEventListener('hashchange', () => this.#restore(), {signal:this.#abort.signal});
    window.addEventListener('pageshow', () => this.#restore(), {signal:this.#abort.signal});
  }
  updatedCallback() {
    super.updatedCallback();
    // Ref changes after editor/section rendering are re-read; our sort only moves existing nodes.
    if (!this.#updating && this.refs.grid && this.#items.some(item => !this.refs.grid.contains(item.node))) this.#initialize();
  }
  disconnectedCallback() { this.#abort?.abort(); super.disconnectedCallback(); }
  #initialize() {
    this.#items = [...this.querySelectorAll('[data-yz-item]')].map((node,index) => {
      const title=node.dataset.title || '';
      const brand=node.dataset.brand || brands.filter(([,regex])=>regex.test(title)).map(([name])=>name).join('|');
      const explicitModels=[...new Set(title.replace(/(?:19|20)\d{2}\s*[–—-]\s*(?:19|20)\d{2}/g,'').match(models) || [])];
      const explicitSystems=[...new Set(title.match(systems) || [])];
      const dates=title.match(/(?:19|20)\d{2}\s*[–—-]\s*(?:19|20)\d{2}/)?.[0] || '';
      const model=node.dataset.model || [...explicitModels, ...explicitSystems].join(' / ') + (dates ? ' · '+dates : '');
      const simple=normalize(title);
      const solution=node.dataset.solution || (/sur devis|installation/.test(simple)?'service':/boitier|module/.test(simple)?'module':/autoradio/.test(simple)?'autoradio':/compteur|hud/.test(simple)?'compteur':/camera|dashcam/.test(simple)?'camera':'autre');
      return {node,index,title,brand,model,solution,price:Number(node.dataset.price),available:node.dataset.available==='true'};
    });
    this.#options(this.refs.brand, this.#items.flatMap(i=>i.brand.split('|')).filter(Boolean));
    const labels={};
    for(const item of this.#items) labels[item.solution]=item.node.dataset.solutionLabel || item.solution;
    this.#options(this.refs.solution,[...new Set(this.#items.map(i=>i.solution))],labels);
    for(const field of [this.refs.brand,this.refs.solution,this.refs.maxPrice,this.refs.stock,this.refs.sort]) if(field)field.disabled=false;
    if(this.refs.panel && matchMedia('(max-width:749px)').matches) this.refs.panel.open=false;
    this.#restore();
  }
  #options(select, values, labels={}) {
    const previous=select.value;
    select.replaceChildren(new Option(this.dataset.allLabel || '', ''));
    for(const value of [...new Set(values)].sort((a,b)=>a.localeCompare(b,'fr'))) select.add(new Option(labels[value] || value,value));
    select.value=[...select.options].some(o=>o.value===previous)?previous:'';
  }
  #models() {
    const brand=this.refs.brand.value;
    this.#options(this.refs.model,this.#items.filter(i=>i.brand.split('|').includes(brand)).map(i=>i.model).filter(Boolean));
    this.refs.model.disabled=!brand || this.refs.model.options.length<2;
  }
  #restore() {
    const params=new URLSearchParams(location.hash.slice(1));
    // Hash is authoritative; going back to a clear URL restores an unfiltered collection.
    this.refs.brand.value=params.get('marque') || '';
    this.#models();this.refs.model.value=params.get('modele') || '';
    this.refs.solution.value=params.get('solution') || '';
    this.refs.maxPrice.value=params.get('prix') || '';
    this.refs.stock.checked=params.get('stock')==='1';
    this.refs.sort.value=params.get('tri') || 'default';
    this.#apply(false);
  }
  /** @param {Event} event - Native select change. */
  changeBrand(event) { if(!(event.target instanceof HTMLSelectElement))return;this.refs.model.value='';this.#models();this.#apply(true); }
  /** @param {Event} _event - Native filter change/input. */
  changeFilters(_event) { this.#apply(true); }
  /** @param {Event} _event - Reset button click. */
  resetFilters(_event) { this.refs.brand.value='';this.refs.model.value='';this.refs.solution.value='';this.refs.maxPrice.value='';this.refs.stock.checked=false;this.refs.sort.value='default';this.#models();this.#apply(true); }
  #apply(persist) {
    const r=this.refs, price=r.maxPrice.value===''?Infinity:Number(r.maxPrice.value)*100;
    let count=0;
    this.#updating=true;
    const ordered=[...this.#items];
    if(r.sort.value==='price-ascending')ordered.sort((a,b)=>a.price-b.price);
    else if(r.sort.value==='price-descending')ordered.sort((a,b)=>b.price-a.price);
    else if(r.sort.value==='title-ascending')ordered.sort((a,b)=>a.title.localeCompare(b.title,'fr'));
    for(const item of ordered){
      const visible=(!r.brand.value||item.brand.split('|').includes(r.brand.value))&&(!r.model.value||item.model===r.model.value)&&(!r.solution.value||item.solution===r.solution.value)&&(!r.stock.checked||item.available)&&item.price<=price;
      item.node.hidden=!visible;if(visible)count++;
      r.grid.append(item.node);
    }
    r.empty.hidden=count>0;
    r.status.textContent=((count===1?this.dataset.singleLabel:this.dataset.countLabel) || '{count}').replace('{count}',String(count));
    const active=[r.brand.value,r.model.value,r.solution.value?r.solution.selectedOptions[0]?.text:'',r.maxPrice.value?`${r.maxPrice.value} ${document.documentElement.lang==='fr'?'€':''}`:'',r.stock.checked?r.stock.parentElement.textContent.trim():''].filter(Boolean);
    r.active.textContent=active.join(' · ');r.active.hidden=!active.length;
    if(persist){const params=new URLSearchParams();for(const[key,value]of Object.entries({marque:r.brand.value,modele:r.model.value,solution:r.solution.value,prix:r.maxPrice.value,stock:r.stock.checked?'1':'',tri:r.sort.value==='default'?'':r.sort.value}))if(value)params.set(key,value);const url=new URL(location.href);url.hash=params.toString();history.replaceState(history.state,'',url);}
    this.#updating=false;
  }
}
if(!customElements.get('yz-vehicle-browser'))customElements.define('yz-vehicle-browser',VehicleBrowser);
